import { useEffect, useState } from "react";
import type { Character } from "../api/character";
import type { LLMModel } from "../api/llm";
import { ModelSelect } from "./model-select";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";

interface CreateCharacterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateCharacter: (
    characterName: string,
    chracterUniqueName: string,
    model: LLMModel,
    initialChatRoomName: string,
  ) => Promise<void>;
  models: LLMModel[];
  characters: Character[];
}

export function CreateCharacterModal({
  isOpen,
  onClose,
  onCreateCharacter,
  models,
  characters,
}: CreateCharacterModalProps) {
  const [characterName, setCharacterName] = useState("");
  const [characterUniqueName, setCharacterUniqueName] = useState("");
  const [selectedModel, setSelectedModel] = useState<LLMModel | null>(null);
  const [initialChatRoomName, setInitialChatRoomName] = useState("");
  const [uniqueNameError, setUniqueNameError] = useState<string | null>(null);

  const validateUniqueName = (name: string): boolean => {
    const regex = /^[a-zA-Z0-9_-]+$/;
    return regex.test(name);
  };

  const handleUniqueNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCharacterUniqueName(value);
    if (value && !validateUniqueName(value)) {
      setUniqueNameError("英数字、ハイフン(-)、アンダーバー(_)のみ使用可能です");
    } else if (characters.some((character) => character.unique_name === value)) {
      setUniqueNameError("すでに存在するユニーク名です");
    } else {
      setUniqueNameError(null);
    }
  };

  useEffect(() => {
    if (models.length > 0 && !selectedModel) {
      setSelectedModel(models[0]);
    }
  }, [models, selectedModel]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      characterName.trim() &&
      characterUniqueName.trim() &&
      selectedModel &&
      initialChatRoomName.trim() &&
      validateUniqueName(characterUniqueName)
    ) {
      await onCreateCharacter(
        characterName.trim(),
        characterUniqueName.trim(),
        selectedModel,
        initialChatRoomName.trim(),
      );
      setCharacterName("");
      setCharacterUniqueName("");
      setInitialChatRoomName("");
      setSelectedModel(models[0]);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>キャラクターを作成</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div>
              <label htmlFor="characterName" className="block text-sm font-medium text-gray-700 mb-1">
                メインキャラクター名
              </label>
              <Input
                id="characterName"
                placeholder="メインキャラクター名を入力"
                value={characterName}
                onChange={(e) => setCharacterName(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="characterUniqueName" className="block text-sm font-medium text-gray-700 mb-1">
                キャラクターのユニーク名
              </label>
              <Input
                id="characterUniqueName"
                placeholder="キャラクターのユニーク名を入力"
                value={characterUniqueName}
                onChange={handleUniqueNameChange}
                className={uniqueNameError ? "border-red-500" : ""}
              />
              {uniqueNameError && <p className="text-sm text-red-500 mt-1">{uniqueNameError}</p>}
            </div>
            <div>
              <label htmlFor="modelSelect" className="block text-sm font-medium text-gray-700 mb-1">
                モデル
              </label>
              {selectedModel && <ModelSelect value={selectedModel} onChange={setSelectedModel} models={models} />}
            </div>
            <div>
              <label htmlFor="initialChatRoomName" className="block text-sm font-medium text-gray-700 mb-1">
                初回データ管理タグ名
              </label>
              <Input
                id="initialChatRoomName"
                placeholder="データ管理タグ名を入力"
                value={initialChatRoomName}
                onChange={(e) => setInitialChatRoomName(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              キャンセル
            </Button>
            <Button
              type="submit"
              disabled={!characterName.trim() || !characterUniqueName.trim() || !selectedModel || !!uniqueNameError}
            >
              作成
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
